import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import SimplePdfViewerIframe from '../components/SimplePdfViewerIframe';
import DynamicReviewForm from '../components/DynamicReviewForm';
import './AuthorThesisPage.css';
import HelpButton from '../components/HelpButton';

/**
 * Страница автора статьи:
 * - drag-n-drop загрузка DOCX + прогресс загрузки;
 * - reconvert в PDF с номерами строк, polling статуса (uploaded/converting/ready/failed);
 * - предпросмотр PDF как blob:ObjectURL (axios берёт авторизацию);
 * - список рецензий (read-only) с поиском по фильтрам и статусу, через аккордеоны.
 */

export default function AuthorThesisPage() {
  // теперь работаем с article_id вместо thesis_id
  const { article_id } = useParams();
  const { getCurrentLanguage, getLocalizedFullName } = useLocale();
  const lang = getCurrentLanguage();
  const navigate = useNavigate();

  // helpers
  const t = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val[lang] || val.ru || val.en || '';
  };

  const stripHtml = (html) => (html ? String(html).replace(/<[^>]*>/g, '') : '');

  // meta + reviews
  const [meta, setMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [errorMeta, setErrorMeta] = useState('');

  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [errorReviews, setErrorReviews] = useState('');

  // pdf
  const [pdfUrl, setPdfUrl] = useState(null); // blob:ObjectURL
  const pdfUrlRef = useRef(null); // revoke старого URL

  // upload/convert state
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const pollRef = useRef(null);
  const submissionReqSeq = useRef(0); // guards against out-of-order / stale responses
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  // Single source of truth: derive status from meta
  const articleStatus = meta?.article?.article?.status || null;

  // delete article (author only)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Upload confirmation (used for identical-version warning)
  const [showUploadConfirm, setShowUploadConfirm] = useState(false);
  const [uploadConfirmMessage, setUploadConfirmMessage] = useState('');
  const pendingUploadRef = useRef(null); // { file: File, uploadUrl: string }

  // edit article meta (author only, only before reviewer assignment)
  const [isEditingMeta, setIsEditingMeta] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [editError, setEditError] = useState('');

  // Draft values (keep both languages so we don't accidentally wipe the other locale)
  const [draftTitle, setDraftTitle] = useState({ ru: '', en: '' });
  const [draftAbstract, setDraftAbstract] = useState({ ru: '', en: '' });

  // drag & drop
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  // reviews UX: поиск/фильтры/аккордеон
  const [reviewQuery, setReviewQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [expandedIds, setExpandedIds] = useState(() => new Set()); // по умолчанию свёрнуто
  const hasAnyReviews = reviews.length > 0;

  // последний комментарий по каждому ревью
  // lastComments[reviewId] = { time: ISOString }
  const [lastComments, setLastComments] = useState({});
  // deadlines per review (key: reviewId)
  const [reviewDeadlines, setReviewDeadlines] = useState({});

  const formatDateTime = (value) => {
    if (!value) return '';
    try {
      const d = new Date(value);
      return d.toLocaleString();
    } catch {
      return String(value);
    }
  };
  const applySubmissionData = (data) => {
    setMeta(data);

    const articleInfo = data?.article || {};
    const articlePayload = articleInfo?.article || {};

    // Keep drafts in sync when not editing
    if (!isEditingMeta) {
      const titleObj = articleInfo?.title || {};
      const absObj = articleInfo?.abstract || {};

      setDraftTitle({
        ru: typeof titleObj === 'object' ? titleObj.ru || '' : '',
        en: typeof titleObj === 'object' ? titleObj.en || '' : '',
      });

      // abstract can include HTML; keep raw (we'll edit as plain text)
      const rawRu = typeof absObj === 'object' ? absObj.ru || '' : '';
      const rawEn = typeof absObj === 'object' ? absObj.en || '' : '';

      setDraftAbstract({
        ru: stripHtml(rawRu),
        en: stripHtml(rawEn),
      });
    }

    const versions = articlePayload?.versions || [];
    const currentVersionId = articlePayload?.current_version_id || null;

    setSelectedVersionId((prev) => {
      const isReady = (id) => id && versions.some((v) => v.id === id && v.status === 'ready');

      if (currentVersionId && isReady(currentVersionId)) return currentVersionId;
      if (prev && isReady(prev)) return prev;

      const lastReady = [...versions].reverse().find((v) => v.status === 'ready');
      return lastReady ? lastReady.id : null;
    });
  };

  // ---- load meta ----
  async function loadMeta() {
    setLoadingMeta(true);
    setErrorMeta('');

    const seq = ++submissionReqSeq.current;

    try {
      const r = await api.get(`/author/submissions/${article_id}`);

      // Ignore stale/out-of-order responses
      if (seq !== submissionReqSeq.current) return;

      applySubmissionData(r.data);
    } catch (e) {
      console.error(e);
      setErrorMeta(lang === 'ru' ? 'Ошибка загрузки данных' : 'Failed to load');
    } finally {
      // Only clear loading if this is still the latest request
      if (seq === submissionReqSeq.current) setLoadingMeta(false);
    }
  }

  // ---- load reviews ----
  async function loadReviews() {
    setLoadingReviews(true);
    setErrorReviews('');
    try {
      // article-based endpoint: /reviews/article/{article_id}/reviews
      const r = await api.get(`/reviews/article/${article_id}/reviews`);
      setReviews(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error(e);
      setErrorReviews(lang === 'ru' ? 'Не удалось загрузить рецензии' : 'Failed to load reviews');
    } finally {
      setLoadingReviews(false);
    }
  }
  const reviewProgress = meta?.review_progress || null;
  const acceptedReviewersTotal =
    reviewProgress && typeof reviewProgress.accepted_total === 'number'
      ? reviewProgress.accepted_total
      : null;
  const finalReviewsSubmitted =
    reviewProgress && typeof reviewProgress.final_submitted === 'number'
      ? reviewProgress.final_submitted
      : null;

  // ---- load pdf blob ----
  async function loadPdfBlob(versionId) {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    try {
      // если передали конкретную версию - используем article_version_id,
      // иначе берём текущую (без query, бэкенд сам подставит current_version)
      const effectiveId =
        typeof versionId === 'number' || typeof versionId === 'string'
          ? versionId
          : selectedVersionId;

      const urlPath =
        effectiveId != null
          ? `/articles/${article_id}.pdf?article_version_id=${effectiveId}`
          : `/articles/${article_id}.pdf`;

      const r = await api.get(urlPath, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      setPdfUrl(url);
      pdfUrlRef.current = url;
    } catch (e) {
      console.error(e);
      setPdfUrl(null);
      pdfUrlRef.current = null;
    }
  }

  useEffect(() => {
    loadMeta();
    loadReviews();

    return () => {
      clearInterval(pollRef.current);
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article_id]);

  // подгружаем PDF при выборе версии
  useEffect(() => {
    if (articleStatus === 'ready') {
      // автоподгрузка PDF для текущей/выбранной версии
      loadPdfBlob();
    }
    // Важно: не сбрасываем pdfUrl в else,
    // чтобы пользователь видел старую версию,
    // пока новая в статусе "converting"/"failed".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleStatus]);

  const formatVersionDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString();
    } catch {
      return String(iso);
    }
  };

  // ---- polling status until ready/failed ----
  const startPollingStatus = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const seq = ++submissionReqSeq.current;

        const r = await api.get(`/author/submissions/${article_id}`);

        // Ignore stale/out-of-order responses
        if (seq !== submissionReqSeq.current) return;

        applySubmissionData(r.data);

        const st = r?.data?.article?.article?.status || null;

        if (st === 'ready') {
          setBusy(false);
          setProgress(100);
          clearInterval(pollRef.current);
          pollRef.current = null;
        } else if (st === 'failed') {
          setBusy(false);
          setProgress(0);
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        console.error(e);
        setBusy(false);
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 5000);
  };

  // ---- upload & reconvert ----
  const onFileSelected = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;

    const hasUploadedVersion = (meta?.article?.article?.versions || []).length > 0;
    const uploadUrl =
      canReplaceFirstVersion && hasUploadedVersion
        ? `/articles/${article_id}/replace-first`
        : `/articles/${article_id}/upload`;

    try {
      setBusy(true);
      setProgress(10);

      const form = new FormData();
      form.append('file', f, f.name);

      await api.post(uploadUrl, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) {
            const p = Math.floor((e.loaded / e.total) * 60);
            setProgress(Math.max(10, p));
          }
        },
      });

      setProgress(70);
      await api.post(`/articles/${article_id}/reconvert`, null, {
        params: { with_line_numbers: 'true', force: 'true' },
      });

      // Optimistically reflect converting in meta (single source of truth)
      setMeta((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        const articleBlock = next.article ? { ...next.article } : {};
        const inner = articleBlock.article ? { ...articleBlock.article } : {};
        inner.status = 'converting';
        articleBlock.article = inner;
        next.article = articleBlock;
        return next;
      });

      setProgress(80);
      startPollingStatus();
    } catch (e) {
      console.error(e);

      const detail = e?.response?.data?.detail;
      const code = detail && typeof detail === 'object' ? detail.code : null;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail && typeof detail === 'object'
            ? detail.message || JSON.stringify(detail)
            : (e?.response?.data && JSON.stringify(e.response.data)) || String(e);

      // Identical upload: ask author to confirm and retry with force_identical=true.
      if (e?.response?.status === 409 && code === 'identical_version') {
        setBusy(false);
        setProgress(0);

        const matchedVersionIndex =
          detail && typeof detail === 'object' ? detail?.identical_to?.version_index : null;

        const localizedMsg =
          lang === 'ru'
            ? `Загруженная версия статьи полностью совпадает с версией #${matchedVersionIndex ?? '-'}. Пожалуйста, проверьте файл и подтвердите, что вы действительно хотите загрузить его.`
            : `Your new article version is completely identical to version #${matchedVersionIndex ?? '-'}. Please check the file and confirm that you really want to upload it.`;

        pendingUploadRef.current = { file: f, uploadUrl };
        setUploadConfirmMessage(localizedMsg);
        setShowUploadConfirm(true);
        return;
      }

      setBusy(false);
      setProgress(0);
      alert(msg);
    } finally {
      ev.target.value = '';
    }
  };

  // drag & drop
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    const fakeEvt = { target: { files: [f], value: '' } };
    onFileSelected(fakeEvt);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const closeUploadConfirm = () => {
    if (busy) return;
    setShowUploadConfirm(false);
    setUploadConfirmMessage('');
    pendingUploadRef.current = null;
  };

  const confirmUploadIdentical = async () => {
    const pending = pendingUploadRef.current;
    if (!pending?.file) {
      closeUploadConfirm();
      return;
    }

    setShowUploadConfirm(false);

    try {
      setBusy(true);
      setProgress(10);

      const form = new FormData();
      form.append('file', pending.file, pending.file.name);

      await api.post(`/articles/${article_id}/upload`, form, {
        params: { force_identical: 'true' },
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) {
            const p = Math.floor((e.loaded / e.total) * 60);
            setProgress(Math.max(10, p));
          }
        },
      });

      setProgress(70);
      await api.post(`/articles/${article_id}/reconvert`, null, {
        params: { with_line_numbers: 'true', force: 'true' },
      });

      // Optimistically reflect converting in meta (single source of truth)
      setMeta((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        const articleBlock = next.article ? { ...next.article } : {};
        const inner = articleBlock.article ? { ...articleBlock.article } : {};
        inner.status = 'converting';
        articleBlock.article = inner;
        next.article = articleBlock;
        return next;
      });

      setProgress(80);
      startPollingStatus();
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail && typeof detail === 'object'
            ? detail.message || JSON.stringify(detail)
            : (e?.response?.data && JSON.stringify(e.response.data)) || String(e);

      setBusy(false);
      setProgress(0);
      alert(msg);
    } finally {
      pendingUploadRef.current = null;
      setUploadConfirmMessage('');
    }
  };

  const norm = (s) => (s || '').toString().toLowerCase();

  // memoed meta
  const festschriftName = useMemo(() => t(meta?.festschrift?.name), [meta, lang]);
  const sectionName = useMemo(() => t(meta?.section?.name), [meta, lang]);
  const articleInfo = meta?.article || {};
  const thesisTitle = useMemo(() => t(articleInfo?.title), [meta, lang]);
  const thesisAbstract = useMemo(() => stripHtml(t(articleInfo?.abstract)), [meta, lang]);
  const permissions = meta?.permissions || {};
  const activeDeadline = meta?.active_deadline || null;

  const isFinalized = useMemo(() => {
    // Primary source of truth: DB flag (works even with 0 reviewers / 0 reviews)
    const locked = Boolean(meta?.article?.article?.workflow_locked);

    // Backward-compatible fallback: older backend might only mark reviews as finalized
    const finalizedByReviews = (reviews || []).some(
      (r) => String(r?.status || '').toLowerCase() === 'finalized',
    );

    return locked || finalizedByReviews;
  }, [meta, reviews]);
  // ---- Review progress hint for uploads ----
  const finalReviewStatuses = new Set(['approved', 'revision_required', 'rejected', 'finalized']);

  // Use meta.reviews (returned by /author/submissions/{id}) as the best "author-view" source of statuses
  const metaReviews = Array.isArray(meta?.reviews) ? meta.reviews : [];

  const approvedFinalCount = useMemo(() => {
    return metaReviews.filter((r) => {
      const st = norm(r?.status);
      return st === 'approved';
    }).length;
  }, [metaReviews]);

  const allFinalSubmitted =
    typeof acceptedReviewersTotal === 'number' &&
    typeof finalReviewsSubmitted === 'number' &&
    acceptedReviewersTotal > 0 &&
    finalReviewsSubmitted >= acceptedReviewersTotal;

  const allApproved =
    allFinalSubmitted &&
    typeof acceptedReviewersTotal === 'number' &&
    approvedFinalCount >= acceptedReviewersTotal;

  const uploadProgressHint = useMemo(() => {
    // 1) If locked/finalized - do not show anything here
    if (isFinalized) {
      return '';
    }

    // If we don't have progress numbers yet, don't show anything (keep UI quiet)
    if (typeof acceptedReviewersTotal !== 'number' || typeof finalReviewsSubmitted !== 'number')
      return '';

    // 2) All approved => upload optional
    if (acceptedReviewersTotal > 0 && allApproved) {
      return lang === 'ru'
        ? `${finalReviewsSubmitted}/${acceptedReviewersTotal} рецензентов одобрили вашу работу. Вы всё ещё можете загрузить новую версию, но это не обязательно.`
        : `${finalReviewsSubmitted}/${acceptedReviewersTotal} reviewers approved your work. You can still upload a new version, but it’s not required.`;
    }

    // 3) All submitted (but not all approved) => can upload now (+ show author upload deadline)
    if (acceptedReviewersTotal > 0 && finalReviewsSubmitted >= acceptedReviewersTotal) {
      const dueAt = activeDeadline?.due_at || null;

      const deadlineSuffix = dueAt
        ? lang === 'ru'
          ? ` Дедлайн: ${formatDateTime(dueAt)}${activeDeadline?.overdue ? ' (просрочено)' : ''}.`
          : ` Deadline: ${formatDateTime(dueAt)}${activeDeadline?.overdue ? ' (overdue)' : ''}.`
        : '';

      return lang === 'ru'
        ? `${finalReviewsSubmitted}/${acceptedReviewersTotal} рецензентов отправили отзывы. Вы можете ознакомиться с ними и загрузить новую версию.${deadlineSuffix}`
        : `${finalReviewsSubmitted}/${acceptedReviewersTotal} reviewers have submitted their reviews. Please review them and submit a new version.${deadlineSuffix}`;
    }

    // 4) Not all submitted yet => cannot upload
    if (acceptedReviewersTotal > 0) {
      return lang === 'ru'
        ? `${finalReviewsSubmitted}/${acceptedReviewersTotal} рецензентов отправили отзывы. Загрузка новой версии будет доступна, когда все рецензенты отправят отзывы.`
        : `${finalReviewsSubmitted}/${acceptedReviewersTotal} reviewers have submitted their reviews. Uploading a new version will be available once all reviewers have submitted their reviews.`;
    }

    return '';
  }, [
    isFinalized,
    lang,
    acceptedReviewersTotal,
    finalReviewsSubmitted,
    allApproved,
    activeDeadline,
  ]);

  const articlePayload = articleInfo?.article || {};
  const article = articlePayload || {};
  const articleVersions = article?.versions || [];
  const reviewFormat = meta?.review_format || 'open';
  const isAuthorViewer = !!permissions.isAuthor;

  const canDeleteArticle = !!permissions.isAuthor;

  // Backend will set canDelete=false when submission is locked.
  // Keep fallback for older backend responses.
  const deleteEnabled =
    permissions?.canDelete !== undefined ? !!permissions.canDelete : canDeleteArticle;

  const deleteLockedTooltip =
    lang === 'ru'
      ? 'Удаление недоступно: обратитесь к редактору, чтобы удалить статью на этом этапе'
      : 'Deletion is disabled: contact an editor to delete the article at this stage';

  const canReplaceFirstVersion = !!permissions.can_replace_first_version;

  const openDeleteConfirm = () => {
    setDeleteError('');
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setShowDeleteConfirm(false);
  };

  const confirmDelete = async () => {
    if (deleting) return;

    setDeleting(true);
    setDeleteError('');

    try {
      // stop polling to avoid race-y reloads while deleting
      clearInterval(pollRef.current);
      pollRef.current = null;

      await api.delete(`/author/submissions/${article_id}`);

      // cleanup local pdf url
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }

      // go back to previous page (stable, no route assumptions)
      navigate(-1);
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.detail ||
        (lang === 'ru' ? 'Не удалось удалить статью' : 'Failed to delete the article');
      setDeleteError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setDeleting(false);
    }
  };

  const startEditMeta = () => {
    setEditError('');
    setIsEditingMeta(true);
  };

  const cancelEditMeta = () => {
    if (savingMeta) return;
    setEditError('');

    // Reset drafts from current meta
    const titleObj = articleInfo?.title || {};
    const absObj = articleInfo?.abstract || {};

    setDraftTitle({
      ru: typeof titleObj === 'object' ? titleObj.ru || '' : '',
      en: typeof titleObj === 'object' ? titleObj.en || '' : '',
    });

    setDraftAbstract({
      ru: stripHtml(typeof absObj === 'object' ? absObj.ru || '' : ''),
      en: stripHtml(typeof absObj === 'object' ? absObj.en || '' : ''),
    });

    setIsEditingMeta(false);
  };

  const saveEditMeta = async () => {
    if (savingMeta) return;

    setSavingMeta(true);
    setEditError('');

    try {
      await api.patch(`/author/submissions/${article_id}`, {
        title_ru: draftTitle.ru,
        title_en: draftTitle.en,
        abstract_ru: draftAbstract.ru,
        abstract_en: draftAbstract.en,
      });

      // Reload meta so UI is consistent with backend + permissions
      await loadMeta();
      setIsEditingMeta(false);
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.detail ||
        (lang === 'ru' ? 'Не удалось сохранить изменения' : 'Failed to save changes');
      setEditError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSavingMeta(false);
    }
  };

  const badgeClass =
    articleStatus === 'ready'
      ? 'badge ok'
      : articleStatus === 'converting'
        ? 'badge warn'
        : articleStatus === 'failed'
          ? 'badge err'
          : 'badge';

  // ---- reviews: derive statuses for chips ----
  const allStatuses = useMemo(() => {
    const s = Array.from(new Set(reviews.map((r) => r?.status).filter(Boolean)));
    return ['all', ...s];
  }, [reviews]);

  // ---- reviews: filtering & search ----
  const filteredReviews = useMemo(() => {
    const q = norm(reviewQuery);

    return reviews.filter((r) => {
      if (selectedStatus !== 'all' && r?.status !== selectedStatus) {
        return false;
      }

      const rawName = getLocalizedFullName?.(r?.author_full_name) || '';
      const email = r?.author_email || '';

      const isAnonymous = !rawName && !email;

      const nameForSearch = isAnonymous
        ? lang === 'ru'
          ? 'Анонимный рецензент'
          : 'Anonymous reviewer'
        : rawName;

      const formText = r?.text ? Object.values(r.text).join(' ') : '';

      const haystack = `${nameForSearch} ${email} ${formText}`.toLowerCase();

      if (!q) return true;

      return haystack.includes(q);
    });
  }, [reviews, reviewQuery, selectedStatus, getLocalizedFullName, lang]);

  const isExpanded = (id) => expandedIds.has(id);
  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const expandAll = () => setExpandedIds(new Set(filteredReviews.map((r) => r.id)));

  const collapseAll = () => setExpandedIds(new Set());

  const getReviewStatusLabel = (status, language) => {
    if (!status) {
      return language === 'ru' ? 'нет статуса' : 'no status';
    }

    const mapping = {
      pending: {
        ru: 'ожидает ответа автора',
        en: 'author response pending',
      },
      rejected: {
        ru: 'отклонена',
        en: 'rejected',
      },
      in_progress: {
        ru: 'в работе',
        en: 'in progress',
      },
      approved: {
        ru: 'одобрено',
        en: 'approved',
      },
      revision_required: {
        ru: 'требуются доработки',
        en: 'revision required',
      },
      submitted: {
        ru: 'отправлено',
        en: 'submitted',
      },
      finalized: {
        ru: 'работа завершена',
        en: 'article is finalized',
      },
    };

    const labels = mapping[status];
    if (!labels) {
      return status;
    }
    return language === 'ru' ? labels.ru : labels.en;
  };

  // открыть страницу с комментариями к конкретному ревью
  const openComments = (reviewId) => {
    const festschriftId = meta?.festschrift?.id;
    if (!festschriftId || !reviewId) return;

    const festName = festschriftName || '';
    const artTitle = thesisTitle || '';

    const encConf = encodeURIComponent(festName);
    const encThesis = encodeURIComponent(artTitle);

    navigate(
      `/publishing/${festschriftId}/review/${reviewId}?viewer=author&editable=False&flow=dashboard&conferenceName=${encConf}&thesisTitle=${encThesis}`,
    );
  };

  const formatArticleStatusLabel = (status, lang) => {
    const value = (status || '').toLowerCase();

    if (!value || value === 'none') {
      return lang === 'ru' ? 'Статья не загружена' : 'Article is not uploaded';
    }
    if (value === 'uploaded') {
      return lang === 'ru' ? 'Статья загружена' : 'Article is uploaded';
    }
    if (value === 'converting') {
      return lang === 'ru'
        ? 'Статья в конвертации, подождите немного'
        : 'Article is converting, wait for a bit';
    }
    if (value === 'ready') {
      return lang === 'ru' ? 'Статья готова к рецензированию' : 'Article is ready for review';
    }
    if (value === 'failed') {
      return lang === 'ru' ? 'Ошибка загрузки статьи' : 'Uploading error';
    }
    // fallback - если вдруг придёт что-то нестандартное
    return status || (lang === 'ru' ? 'Без статуса' : 'No status');
  };

  // при каждом обновлении списка рецензий / статуса пересчитываем "последний комментарий" по каждой
  useEffect(() => {
    let cancelled = false;

    async function fetchLastComments() {
      if (!reviews || reviews.length === 0) {
        setLastComments({});
        return;
      }

      try {
        const entries = await Promise.all(
          reviews.map(async (rv) => {
            try {
              const resp = await api.get(`/comments/${rv.id}`);
              const comments = Array.isArray(resp.data) ? resp.data : [];

              let lastMessage = null;

              for (const c of comments) {
                const thread = Array.isArray(c.thread) ? c.thread : [];
                for (const msg of thread) {
                  if (!lastMessage) {
                    lastMessage = msg;
                  } else if (new Date(msg.time) > new Date(lastMessage.time)) {
                    lastMessage = msg;
                  }
                }
              }

              if (!lastMessage) {
                return [rv.id, null];
              }

              return [rv.id, { time: lastMessage.time }];
            } catch (e) {
              console.error(e);
              return [rv.id, null];
            }
          }),
        );

        if (cancelled) return;

        const map = {};
        for (const [id, value] of entries) {
          map[id] = value;
        }
        setLastComments(map);
      } catch (e) {
        console.error(e);
      }
    }

    fetchLastComments();

    return () => {
      cancelled = true;
    };
  }, [reviews]);

  // fetch deadlines per review (display only)
  useEffect(() => {
    let cancelled = false;

    async function fetchDeadlines() {
      if (!reviews || reviews.length === 0) {
        setReviewDeadlines({});
        return;
      }

      try {
        const entries = await Promise.all(
          reviews.map(async (rv) => {
            try {
              const res = await api.get(`/reviews/${rv.id}/deadline`);
              return [rv.id, res.data || null];
            } catch {
              return [rv.id, null];
            }
          }),
        );

        if (cancelled) return;

        const map = {};
        for (const [id, d] of entries) {
          if (d && d.due_at) map[id] = d;
        }
        setReviewDeadlines(map);
      } catch (e) {
        if (!cancelled) setReviewDeadlines({});
      }
    }

    fetchDeadlines();
    return () => {
      cancelled = true;
    };
  }, [reviews]);

  // ---- render ----
  return (
    <div className="author-layout">
      <div className="author-scroll">
        <div className="author-thesis-page">
          <h1 className="page-title">{lang === 'ru' ? 'Страница автора' : 'Author page'}</h1>

          {/* Информация о заявке */}
          <div className="section-card">
            <div className="card-head card-head-row">
              <div className="card-head-title">
                {lang === 'ru' ? 'Информация о заявке' : 'Submission info'}
              </div>

              {!loadingMeta && !errorMeta && meta ? (
                <div className="card-head-actions">
                  {permissions?.can_edit ? (
                    !isEditingMeta ? (
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={startEditMeta}
                        aria-label={lang === 'ru' ? 'Редактировать' : 'Edit'}
                        title={lang === 'ru' ? 'Редактировать' : 'Edit'}
                      >
                        {/* edit icon */}
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 20h9"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="icon-btn ok"
                          onClick={saveEditMeta}
                          disabled={savingMeta}
                          aria-label={lang === 'ru' ? 'Сохранить' : 'Save'}
                          title={lang === 'ru' ? 'Сохранить' : 'Save'}
                        >
                          {/* check icon */}
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M20 6L9 17l-5-5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>

                        <button
                          type="button"
                          className="icon-btn"
                          onClick={cancelEditMeta}
                          disabled={savingMeta}
                          aria-label={lang === 'ru' ? 'Отмена' : 'Cancel'}
                          title={lang === 'ru' ? 'Отмена' : 'Cancel'}
                        >
                          {/* x icon */}
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M18 6L6 18M6 6l12 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </>
                    )
                  ) : null}

                  {canDeleteArticle ? (
                    <button
                      type="button"
                      className={`icon-btn danger ${deleteEnabled ? '' : 'locked-disabled'}`}
                      onClick={() => {
                        if (!deleteEnabled) return;
                        openDeleteConfirm();
                      }}
                      aria-label={lang === 'ru' ? 'Удалить статью' : 'Delete article'}
                      title={
                        deleteEnabled
                          ? lang === 'ru'
                            ? 'Удалить статью'
                            : 'Delete article'
                          : deleteLockedTooltip
                      }
                      aria-disabled={!deleteEnabled}
                    >
                      {/* trash icon */}
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M9 3h6m-8 4h10m-9 0v14a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V7M10 11v8M14 11v8"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="card-body">
              {loadingMeta ? (
                <div className="loading-state">{lang === 'ru' ? 'Загрузка…' : 'Loading…'}</div>
              ) : errorMeta ? (
                <div className="error-state">{errorMeta}</div>
              ) : !meta ? (
                <div className="error-state">
                  {lang === 'ru' ? 'Данные по заявке не найдены' : 'Submission data not found'}
                </div>
              ) : (
                <>
                  <div className="info-grid">
                    <div>
                      <b>{lang === 'ru' ? 'Сборник' : 'Festschrift'}:</b>{' '}
                      <span>{festschriftName || '-'}</span>
                    </div>
                    <div>
                      <b>{lang === 'ru' ? 'Секция' : 'Section'}:</b>{' '}
                      <span>{sectionName || '-'}</span>
                    </div>

                    {!isEditingMeta ? (
                      <div>
                        <b>{lang === 'ru' ? 'Заголовок' : 'Title'}:</b>
                        <span>{thesisTitle || '-'}</span>
                      </div>
                    ) : (
                      <div className="edit-grid">
                        <div className="edit-field">
                          <label className="edit-label">Title (RU)</label>
                          <input
                            type="text"
                            className="edit-input"
                            value={draftTitle.ru}
                            onChange={(e) => setDraftTitle((p) => ({ ...p, ru: e.target.value }))}
                          />
                        </div>
                        <div className="edit-field">
                          <label className="edit-label">Title (EN)</label>
                          <input
                            type="text"
                            className="edit-input"
                            value={draftTitle.en}
                            onChange={(e) => setDraftTitle((p) => ({ ...p, en: e.target.value }))}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {!isEditingMeta ? (
                    <div className="abstract-block">
                      <b>{lang === 'ru' ? 'Аннотация' : 'Abstract'}:</b>
                      <div className="muted">{thesisAbstract || '-'}</div>
                    </div>
                  ) : (
                    <div className="edit-grid edit-grid-abstract">
                      <div className="edit-field">
                        <label className="edit-label">Abstract (RU)</label>
                        <textarea
                          className="edit-textarea"
                          rows={5}
                          value={draftAbstract.ru}
                          onChange={(e) => setDraftAbstract((p) => ({ ...p, ru: e.target.value }))}
                        />
                      </div>
                      <div className="edit-field">
                        <label className="edit-label">Abstract (EN)</label>
                        <textarea
                          className="edit-textarea"
                          rows={5}
                          value={draftAbstract.en}
                          onChange={(e) => setDraftAbstract((p) => ({ ...p, en: e.target.value }))}
                        />
                      </div>

                      {editError ? <div className="edit-error">{editError}</div> : null}

                      {!permissions?.can_edit ? (
                        <div className="muted">
                          {lang === 'ru'
                            ? 'Редактирование недоступно после назначения рецензента.'
                            : 'Editing is disabled once a reviewer is assigned.'}
                        </div>
                      ) : null}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Список рецензий */}
          <div className="section-card">
            <div className="card-head author-thesis-card-head">
              <span>{lang === 'ru' ? 'Рецензии' : 'Reviews'}</span>
              <HelpButton helpId="author.thesis.reviews" helpLabel="Подсказка" />
            </div>
            <div className="card-body">
              {!permissions?.canSeeReviews ? (
                <div className="muted">
                  {lang === 'ru'
                    ? 'У вас нет прав на просмотр рецензий.'
                    : 'You do not have permission to view reviews.'}
                </div>
              ) : (
                <>
                  <div className="review-toolbar">
                    <input
                      className="review-search-input"
                      type="text"
                      value={reviewQuery}
                      onChange={(e) => setReviewQuery(e.target.value)}
                      placeholder={
                        lang === 'ru'
                          ? 'Поиск по рецензенту и тексту…'
                          : 'Search by reviewer and text…'
                      }
                    />
                    <div className="chips">
                      {allStatuses.map((st) => (
                        <button
                          key={st}
                          className={`chip ${selectedStatus === st ? 'active' : ''}`}
                          onClick={() => setSelectedStatus(st)}
                        >
                          {st === 'all'
                            ? lang === 'ru'
                              ? 'Все'
                              : 'All'
                            : getReviewStatusLabel(st, lang)}
                        </button>
                      ))}
                    </div>
                    {hasAnyReviews && (
                      <div className="review-toolbar-right">
                        <button className="btn sm ghost" onClick={expandAll}>
                          {lang === 'ru' ? 'Развернуть все' : 'Expand all'}
                        </button>
                        <button className="btn sm" onClick={collapseAll}>
                          {lang === 'ru' ? 'Свернуть все' : 'Collapse all'}
                        </button>
                      </div>
                    )}
                  </div>

                  {loadingReviews ? (
                    <div className="loading-state">{lang === 'ru' ? 'Загрузка…' : 'Loading…'}</div>
                  ) : errorReviews ? (
                    <div className="error-state">{errorReviews}</div>
                  ) : hasAnyReviews ? (
                    filteredReviews.length ? (
                      <div className="reviews-list">
                        {filteredReviews.map((rv) => {
                          const rid = rv.id;
                          const expanded = isExpanded(rid);

                          const localizedName = getLocalizedFullName?.(rv?.author_full_name) || '';
                          const email = rv?.author_email || '';

                          const isReviewerAnonymous = !localizedName && !email;

                          const displayName = isReviewerAnonymous
                            ? lang === 'ru'
                              ? 'Анонимный рецензент'
                              : 'Anonymous reviewer'
                            : localizedName || email || '-';

                          const statusLabel = getReviewStatusLabel(rv?.status, lang);
                          const lastStatusChange = rv?.last_status_change;
                          const lastCommentInfo = lastComments[rid];
                          const dl = reviewDeadlines[rid] || null;

                          return (
                            <div key={rid} className={`review-block ${expanded ? 'expanded' : ''}`}>
                              <div
                                className="review-header as-button"
                                onClick={() => toggleExpanded(rid)}
                                aria-expanded={expanded}
                                role="button"
                              >
                                <div className="review-header-left">
                                  <div className="review-author">{displayName}</div>
                                  <div className="review-meta-line">
                                    <span className="review-status">{statusLabel}</span>
                                    {dl?.due_at && (
                                      <>
                                        <span className="meta-dot">•</span>
                                        <span
                                          className={
                                            'meta-note review-deadline' +
                                            (dl.overdue ? ' overdue' : '')
                                          }
                                        >
                                          {(() => {
                                            const role = (dl.role || '').toLowerCase();

                                            const roleLabel =
                                              role === 'author'
                                                ? lang === 'ru'
                                                  ? 'дедлайн автора'
                                                  : 'author deadline'
                                                : role === 'reviewer'
                                                  ? lang === 'ru'
                                                    ? 'дедлайн рецензента'
                                                    : 'reviewer deadline'
                                                  : lang === 'ru'
                                                    ? 'дедлайн'
                                                    : 'deadline';

                                            return `${roleLabel}: `;
                                          })()}
                                          {formatDateTime(dl.due_at)}
                                          {dl.overdue
                                            ? lang === 'ru'
                                              ? ' (просрочено)'
                                              : ' (overdue)'
                                            : ''}
                                        </span>
                                      </>
                                    )}

                                    {lastStatusChange && (
                                      <>
                                        <span className="meta-dot">•</span>
                                        <span className="meta-note">
                                          {lang === 'ru' ? 'статус обновлён ' : 'status updated '}
                                          {formatDateTime(lastStatusChange)}
                                        </span>
                                      </>
                                    )}
                                    {lastCommentInfo && lastCommentInfo.time && (
                                      <>
                                        <span className="meta-dot">•</span>
                                        <span className="meta-note">
                                          {lang === 'ru'
                                            ? 'последний комментарий '
                                            : 'last comment '}
                                          {formatDateTime(lastCommentInfo.time)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>

                                <div className="review-header-right">
                                  <div className="review-header-actions">
                                    <button
                                      type="button"
                                      className="btn sm secondary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpanded(rid);
                                      }}
                                    >
                                      {expanded
                                        ? lang === 'ru'
                                          ? 'Скрыть отзыв'
                                          : 'Hide review'
                                        : lang === 'ru'
                                          ? 'Показать отзыв'
                                          : 'Show review'}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openComments(rid);
                                      }}
                                    >
                                      {lang === 'ru' ? 'Комментарии' : 'Comments'}
                                    </button>
                                  </div>
                                  <span className={`acc-arrow ${expanded ? 'rot' : ''}`}>▾</span>
                                </div>
                              </div>

                              {expanded && (
                                <div className="review-form-container">
                                  <DynamicReviewForm
                                    template={rv?.template?.blocks || []}
                                    formValues={rv?.text || {}}
                                    editable={false}
                                    onChange={() => {}}
                                    reviewFormat={reviewFormat}
                                    viewerRole={isAuthorViewer ? 'author' : 'reviewer'}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="no-reviews">
                        {lang === 'ru' ? 'Ничего не найдено' : 'Nothing found'}
                      </div>
                    )
                  ) : (
                    <div className="no-reviews">
                      {articleStatus === 'none' || articleStatus === 'failed' || !articleStatus
                        ? lang === 'ru'
                          ? 'Работа еще не загружена. Сначала загрузите работу'
                          : 'Work not uploaded yet. Upload the work first'
                        : lang === 'ru'
                          ? 'Работа загружена, но рецензенты еще не назначены. Дождитесь назначения рецензентов'
                          : 'Work is uploaded, but no reviewers yet. Wait for reviewers to be assigned'}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Загрузка DOCX + статус  */}
          <div className="section-card">
            <div className="card-head author-thesis-card-head">
              <span>{lang === 'ru' ? 'Загрузка статьи' : 'Article upload'}</span>
              <HelpButton helpId="author.thesis.upload" helpLabel="Подсказка" />
            </div>
            <div className="card-body">
              {/* Строка статуса, как в старой версии */}
              <div className="status-line">
                <span className={badgeClass}>
                  {lang === 'ru' ? 'Статус загрузки' : 'Upload status'}:{' '}
                  {formatArticleStatusLabel(articleStatus, lang)}
                </span>
                {article?.converted_at && (
                  <span className="small">
                    {lang === 'ru' ? 'конвертировано: ' : 'converted at: '}
                    {formatDateTime(article.converted_at)}
                  </span>
                )}
              </div>
              {!!uploadProgressHint && (
                <div className="small hint" style={{ marginTop: 8 }}>
                  {uploadProgressHint}
                </div>
              )}

              {/* Список версий статьи (старый layout: article-versions + versions-list) */}
              {articleVersions.length > 0 && (
                <div className="article-versions">
                  <div className="versions-title">{lang === 'ru' ? 'Версии' : 'Versions'}</div>
                  <div className="versions-list">
                    {articleVersions.map((v) => {
                      const isSelected = v.id === selectedVersionId;
                      const hasPdf = v.status === 'ready'; // new-world equivalent of v.has_pdf

                      return (
                        <button
                          key={v.id}
                          type="button"
                          className={'version-pill' + (isSelected ? ' active' : '')}
                          onClick={() => {
                            // Пока версия не готова – не переключаемся
                            if (!hasPdf) {
                              return;
                            }
                            setSelectedVersionId(v.id);
                            loadPdfBlob(v.id);
                          }}
                        >
                          <span className="version-main">
                            <span className="version-label">v{v.version_index}</span>
                            {v.created_at && (
                              <span className="version-date">
                                {formatVersionDate(v.created_at)}
                              </span>
                            )}
                            {v.is_current && (
                              <span className="tag-current">
                                {lang === 'ru' ? 'Текущая' : 'Current'}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Зона загрузки .docx (старый layout: uploader + dropzone) */}
              {isFinalized && (
                <div className="finalized-notice">
                  {lang === 'ru'
                    ? 'Работа над статьёй была завершена редактором. Новые версии больше не принимаются. Пожалуйста, ожидайте дальнейшей информации от организаторов'
                    : 'This article has been set as finalized by an editor. New versions are no longer accepted. Please wait for further information from the organizers.'}
                </div>
              )}
              {(permissions.canReupload || canReplaceFirstVersion) && !isFinalized && (
                <div className="uploader">
                  <div
                    className={`dropzone ${dragOver ? 'dragover' : ''}`}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                  >
                    {lang === 'ru' ? (
                      <>
                        {articleVersions.length > 0 && canReplaceFirstVersion ? (
                          <>
                            Перезагрузите <b>.docx</b> (заменит текущий файл) или{' '}
                          </>
                        ) : (
                          <>
                            Перетащите <b>.docx</b> сюда или{' '}
                          </>
                        )}
                        <button
                          type="button"
                          className="btn"
                          onClick={() => fileRef.current?.click()}
                          disabled={busy}
                        >
                          Выберите файл
                        </button>
                      </>
                    ) : (
                      <>
                        {articleVersions.length > 0 && canReplaceFirstVersion ? (
                          <>
                            Re-upload a <b>.docx</b> (replaces current file) or{' '}
                          </>
                        ) : (
                          <>
                            Drag a <b>.docx</b> here or{' '}
                          </>
                        )}
                        <button
                          type="button"
                          className="btn"
                          onClick={() => fileRef.current?.click()}
                          disabled={busy}
                        >
                          Choose file
                        </button>
                      </>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".docx"
                      style={{ display: 'none' }}
                      onChange={onFileSelected}
                    />
                  </div>

                  {(busy || articleStatus === 'converting') && (
                    <div className="progress-area">
                      <div className="progress-wrap">
                        <div className="progress-bar" style={{ width: `${progress}%` }} />
                      </div>
                      <div className="small hint">
                        {lang === 'ru'
                          ? 'Конвертация может занять до 30–40 секунд для больших файлов…'
                          : 'Conversion can take 30–40 seconds for large files…'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* PDF превью - отдельная карточка, как в оригинале */}
          <div className="section-card">
            <div className="card-head">{lang === 'ru' ? 'Предпросмотр PDF' : 'PDF preview'}</div>
            <div className="card-body">
              {pdfUrl ? (
                // ВАЖНО: используем prop src, не pdfUrl
                <SimplePdfViewerIframe src={pdfUrl} height={720} />
              ) : articleStatus === 'converting' ? (
                <div className="loading-state">
                  {lang === 'ru' ? 'Конвертация…' : 'Converting…'}
                </div>
              ) : (
                <div className="no-reviews">
                  {lang === 'ru' ? 'PDF недоступен' : 'PDF is not available'}
                </div>
              )}
            </div>
          </div>

          {/* Delete confirmation modal */}
          {showDeleteConfirm && (
            <div
              className="modal-overlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                // click outside closes
                if (e.target === e.currentTarget) closeDeleteConfirm();
              }}
            >
              <div className="modal">
                <div className="modal-title">
                  {lang === 'ru' ? 'Удалить статью?' : 'Delete the article?'}
                </div>

                <div className="modal-text">
                  {lang === 'ru'
                    ? 'Это действие необратимо. Будут удалены статья, её версии, связанные рецензии и комментарии.'
                    : 'This action cannot be undone. The article, its versions, related reviews and comments will be deleted.'}
                </div>

                {deleteError ? <div className="modal-error">{deleteError}</div> : null}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={closeDeleteConfirm}
                    disabled={deleting}
                  >
                    {lang === 'ru' ? 'Отмена' : 'Cancel'}
                  </button>

                  <button
                    type="button"
                    className="btn danger"
                    onClick={confirmDelete}
                    disabled={deleting}
                  >
                    {deleting
                      ? lang === 'ru'
                        ? 'Удаление...'
                        : 'Deleting...'
                      : lang === 'ru'
                        ? 'Удалить'
                        : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Upload confirmation modal (identical version) */}
          {showUploadConfirm && (
            <div
              className="modal-overlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closeUploadConfirm();
              }}
            >
              <div className="modal">
                <div className="modal-title">
                  {lang === 'ru' ? 'Загрузить одинаковую версию?' : 'Upload an identical version?'}
                </div>

                <div className="modal-text">
                  {uploadConfirmMessage ||
                    (lang === 'ru'
                      ? 'Загруженный .docx совпадает с одной из существующих версий. Подтвердите загрузку.'
                      : 'The uploaded .docx is identical to an existing version. Please confirm the upload.')}
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={closeUploadConfirm}
                    disabled={busy}
                  >
                    {lang === 'ru' ? 'Отмена' : 'Cancel'}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={confirmUploadIdentical}
                    disabled={busy}
                  >
                    {lang === 'ru' ? 'Загрузить' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
